import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { fetchdetailedSummaryCharger, fetchBPLiveDataForCustomer, fetchBPEqpCustkWh, fetchdetailedSummaryChargerForSite, fetchFactoryApiResponse, fetchBPLiveDataForCustomerForOneSite, fetchSessionsForCustomer } from '../services/dashboardService';
import type { detailedChargerDataInterface } from '../types/chargerTypes';
import type { detailedGeneratorData, Equipment, NewApiResponse } from '../types/powerTypes';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import type { KwhData } from '../types';
import { includes } from 'lodash';

/**
 * DashboardSlice - Manages raw API data for dashboard views
 * - Charger data (customer-level and site-level)
 * - Generator data (customer-level and site-level)
 * - Factory dashboard data
 */
interface DashboardState {
    // Charger data
    detailedChargerData: detailedChargerDataInterface;
    loadingDetailedChargerData: boolean;
    loadingSessions: boolean;
    detailedChargerDataForSite: detailedChargerDataInterface;
    chargersId: number[];
    loadingDetailedChargerDataForSite: boolean;
    error: string | null;

    kwhCustomerData: KwhData;
    loadingKwhCustomerData: boolean;
    errorKwhCustomerData: string | null;

    // Generator data
    detailedGeneratorData: detailedGeneratorData[];
    detailedGeneratorDataForMQTT: detailedGeneratorData[];
    loadingGenDetails: boolean;
    errorGenDetails: string | null;
    detailedGeneratorDataForSite: detailedGeneratorData[];
    detailedGeneratorDataForSiteForMQTT: detailedGeneratorData[];
    loadingGenDetailsForSite: boolean;
    errorGenDetailsForSite: string | null;

    // Factory data
    factoryApiResponse: NewApiResponse[];
    loadingFactoryApiResponse: boolean;
    factoryApiResponseError: string | null;
    responses: detailedGeneratorData[][];

    // Generic loading states
    loading: boolean;
    loadingData: boolean;
    canceled: boolean;

    generatorList: Equipment[];
    generatorListForMQTT: Equipment[];
    fuelGenList: Equipment[];
    emptyGenId: boolean;
    emptyGenIdForMQTT: boolean;
    emptyFuelId: boolean;
    generatorListError: string;


}

// Initialize the state
const initialState: DashboardState = {
    // Charger data
    detailedChargerData: { session: [], connector: [], all_connected_chargers: [] },
    loadingDetailedChargerData: false,
    loadingSessions: false,
    detailedChargerDataForSite: { session: [], connector: [], all_connected_chargers: [] },
    chargersId: [],
    loadingDetailedChargerDataForSite: false,
    error: null,
    kwhCustomerData: { month_start: '', current_datetime: '', total_kwh_generated: 0, sites: [] },
    loadingKwhCustomerData: false,
    errorKwhCustomerData: null,
    // Generator data
    detailedGeneratorData: [],
    detailedGeneratorDataForMQTT: [],
    loadingGenDetails: false,
    errorGenDetails: null,
    detailedGeneratorDataForSite: [],
    detailedGeneratorDataForSiteForMQTT: [],
    loadingGenDetailsForSite: false,
    errorGenDetailsForSite: null,

    // Factory data
    factoryApiResponse: [],
    loadingFactoryApiResponse: false,
    factoryApiResponseError: null,
    responses: [],
    canceled: false,

    // Generic loading states
    loading: false,
    loadingData: false,

    generatorList: [],
    generatorListForMQTT: [],
    fuelGenList: [],
    emptyGenId: false,
    emptyGenIdForMQTT: false,
    emptyFuelId: false,
    generatorListError: '',
};

// Create the slice
const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        clearDashboardData(state) {
            state.chargersId = [];
            state.detailedChargerData = { session: [], connector: [], all_connected_chargers: [] };
            state.detailedChargerDataForSite = { session: [], connector: [], all_connected_chargers: [] };
            state.detailedGeneratorData = [];
            state.detailedGeneratorDataForMQTT = [];
            state.loadingKwhCustomerData = false;
            state.errorKwhCustomerData = null;
            state.kwhCustomerData = { month_start: '', current_datetime: '', total_kwh_generated: 0, sites: [] };
            state.detailedGeneratorDataForSite = [];
            state.detailedGeneratorDataForSiteForMQTT = [];
            state.error = null;
            state.errorGenDetails = null;
            state.errorGenDetailsForSite = null;
            state.loadingDetailedChargerData = false;
            state.loadingDetailedChargerDataForSite = false;
            state.loadingGenDetails = false;
            state.loadingGenDetailsForSite = false;
        },
        clearPowerFuelData(state) {
            state.emptyFuelId = false;
            state.emptyGenId = false;
            state.emptyGenIdForMQTT = false;
            state.fuelGenList = [];
            state.generatorList = [];
            state.generatorListForMQTT = [];
            state.generatorListError = '';
            state.detailedGeneratorDataForSite = [];
            state.detailedGeneratorDataForSiteForMQTT = []
            state.detailedChargerDataForSite = { session: [], connector: [], all_connected_chargers: [] };
            state.loadingGenDetailsForSite = false;
            state.errorGenDetailsForSite = null;
            state.loadingDetailedChargerDataForSite = false;
            state.error = null;
        },
        clearGeneralData(state) {
            state.detailedChargerDataForSite = { session: [], connector: [], all_connected_chargers: [] };
            state.detailedGeneratorDataForSite = [];
        },
        setResponses(state, action) {
            state.responses = action.payload;
        },
        clearResponses(state) {
            state.responses = [];
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload
        },
        setLoadingData(state, action: PayloadAction<boolean>) {
            state.loadingData = action.payload
        },
        updateCustomerResponse(
            state,
            action: PayloadAction<{ data: detailedGeneratorData[]; customer: string }>
        ) {
            const customerDataMap = new Map(
                state.responses.map((group, index) => [group[0]?.requested_schema, index])
            );

            const index = customerDataMap.get(action.payload.customer);

            if (index !== undefined) {
                if (!isEqual(state.responses[index], action.payload.data)) {
                    state.responses[index] = action.payload.data;
                }
            } else {
                state.responses.push(action.payload.data);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBPLiveDataForCustomerForOneSite.fulfilled, (state, action: PayloadAction<detailedGeneratorData[]>) => {
                // Extract generator lists from detailed data stored in dashboardSlice
                const allEquipment: Equipment[] = [];
                action.payload.forEach(siteData => {
                    siteData.equipment_data?.forEach(equipment => {
                        if (equipment.data && equipment.data.length > 0) {
                            equipment.data.forEach(dataItem => {
                                const equipmentInfo: Equipment = {
                                    equipment_id: equipment.equipment_id,
                                    equipment_name: dataItem.equipment_name,
                                    source_id: dataItem.source_id
                                };
                                if (!allEquipment.find(e =>
                                    e.equipment_id === equipmentInfo.equipment_id &&
                                    e.source_id === equipmentInfo.source_id
                                )) {
                                    allEquipment.push(equipmentInfo);
                                }
                            });
                        }
                    });
                });

                if (allEquipment.length > 0) {
                    const excludedSources = ['bluepillar', 'BCH-Fuel'];

                    // API-based generators (bluepillar + BCH-Fuel)
                    const apiGenerators = allEquipment.filter(item =>
                        excludedSources.includes(item.source_id)
                    );

                    // MQTT-based generators (telemetry only)
                    const mqttGenerators = allEquipment.filter(item =>
                        item.source_id.includes('telemetry')
                    );

                    // Fuel list logic (special case)
                    const bchFuelEquipment = apiGenerators.filter(
                        item => item.source_id === 'BCH-Fuel'
                    );

                    state.fuelGenList =
                        bchFuelEquipment.length > 0
                            ? bchFuelEquipment
                            : apiGenerators;

                    // Generator lists (split)
                    state.generatorList = apiGenerators.filter(
                        item => item.source_id !== 'BCH-Fuel'
                    );;
                    state.generatorListForMQTT = mqttGenerators;

                    // Empty flags
                    state.emptyFuelId = state.fuelGenList.length === 0;
                    state.emptyGenId = apiGenerators.length === 0;
                    state.emptyGenIdForMQTT = mqttGenerators.length === 0;


                    /**
                     * Site-level GENERATOR data
                     * ONLY bluepillar / BCH-Fuel
                     */
                    state.detailedGeneratorDataForSite = action.payload
                        .map((item) => ({
                            ...item,
                            equipment_data: item.equipment_data
                                ?.map((equipment) => ({
                                    ...equipment,
                                    data: equipment.data?.filter((d) =>
                                        excludedSources.includes(d.source_id)
                                    )
                                }))
                                .filter((equipment) => equipment.data?.length)
                        }))
                        .filter((item) => item.equipment_data?.length);

                    /**
                     * Site-level CHARGER data (MQTT)
                     * ONLY telemetry
                     */
                    state.detailedGeneratorDataForSiteForMQTT = action.payload
                        .map((item) => ({
                            ...item,
                            equipment_data: item.equipment_data
                                ?.map((equipment) => ({
                                    ...equipment,
                                    data: equipment.data?.filter((d) =>
                                        d.source_id.includes('telemetry')
                                    )
                                }))
                                .filter((equipment) => equipment.data?.length)
                        }))
                        .filter((item) => item.equipment_data?.length);


                    state.loadingGenDetailsForSite = false;
                    state.errorGenDetailsForSite = null;
                } else {
                    state.generatorList = [];
                    state.generatorListForMQTT = [];
                    state.fuelGenList = [];
                    state.emptyFuelId = true;
                    state.emptyGenId = true;
                    state.emptyGenIdForMQTT = true;
                    state.detailedGeneratorDataForSite = [];
                    state.detailedGeneratorDataForSiteForMQTT = [];
                    state.loadingGenDetailsForSite = false;
                    state.errorGenDetailsForSite = 'No generators available';
                }
                state.generatorListError = allEquipment.length === 0 ? 'No generators available' : '';
            })
            .addCase(fetchBPLiveDataForCustomerForOneSite.rejected, (state, action) => {
                if (action.payload && (action.payload as any).canceled) {
                    state.canceled = true;
                } else {
                    state.error = action.payload as string;
                    state.loadingGenDetailsForSite = false;
                    state.errorGenDetailsForSite = action.payload as string;
                }
            })
            // Pending: Set loading to true
            .addCase(fetchBPLiveDataForCustomerForOneSite.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.detailedGeneratorDataForSite.length === 0) {
                    state.loadingGenDetailsForSite = true;
                }
            })
            .addCase(fetchdetailedSummaryCharger.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.detailedChargerData.all_connected_chargers.length === 0 &&
                    state.detailedChargerData.session.length === 0) {
                    state.loadingDetailedChargerData = true;
                }
                state.error = null;
            })
            // Fulfilled: Set loading to false and store the fetched data
            .addCase(fetchdetailedSummaryCharger.fulfilled, (state, action: PayloadAction<detailedChargerDataInterface>) => {
                state.loadingDetailedChargerData = false; // Loading ends
                state.detailedChargerData = action.payload; // Store session data
            })
            // Rejected: Set loading to false and store the error message
            .addCase(fetchdetailedSummaryCharger.rejected, (state, action) => {
                state.error = action.payload || 'Failed to fetch session data';
                if (action.payload && (action.payload === 'Request canceled')) {
                    // Keep loading state if request was intentionally canceled
                    state.loadingDetailedChargerData = state.detailedChargerData.all_connected_chargers.length === 0;
                }
                else state.loadingDetailedChargerData = false;
            })
            // Sessions API handlers
            .addCase(fetchSessionsForCustomer.pending, (state) => {
              
                // Only show loading on initial fetch (when data is empty)
                if (state.detailedChargerData.session.length === 0) {
                    state.loadingSessions = true;
                }
            })
            .addCase(fetchSessionsForCustomer.fulfilled, (state, action: PayloadAction<any[]>) => {
               
                // Update sessions in detailedChargerData
                state.detailedChargerData.session = action.payload;
                state.loadingSessions = false;
            })
            .addCase(fetchSessionsForCustomer.rejected, (state, action) => {
                console.warn('[dashboardSlice] fetchSessionsForCustomer REJECTED', {
                    payload: action.payload,
                    isCanceled: action.payload === 'Request canceled',
                    currentSessionLength: state.detailedChargerData.session.length,
                });
                // On error, keep existing session data or empty array
                if (action.payload !== 'Request canceled') {
                    state.error = action.payload || 'Failed to fetch sessions';
                    state.loadingSessions = false;
                } else {
                    // Keep loading state if request was intentionally canceled
                    state.loadingSessions = state.detailedChargerData.session.length === 0;
                }
            })
            .addCase(fetchdetailedSummaryChargerForSite.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.detailedChargerDataForSite.all_connected_chargers.length === 0 &&
                    state.detailedChargerDataForSite.session.length === 0) {
                    state.loadingDetailedChargerDataForSite = true;
                }
                state.error = null;
            })
            // Fulfilled: Set loading to false and store the fetched data
            .addCase(fetchdetailedSummaryChargerForSite.fulfilled, (state, action: PayloadAction<detailedChargerDataInterface>) => {
                state.loadingDetailedChargerDataForSite = false; // Loading ends
                state.detailedChargerDataForSite = action.payload; // Store session data
                state.chargersId = compact(map(action.payload.all_connected_chargers, "id"));
            })
            // Rejected: Set loading to false and store the error message
            .addCase(fetchdetailedSummaryChargerForSite.rejected, (state, action) => {
                state.error = action.payload || 'Failed to fetch session data';
                if (action.payload && (action.payload === 'Request canceled')) {
                    // Keep loading state if request was intentionally canceled and no data exists
                    state.loadingDetailedChargerDataForSite = state.detailedChargerDataForSite.all_connected_chargers.length === 0;
                }
                else state.loadingDetailedChargerDataForSite = false;
            }).addCase(fetchBPLiveDataForCustomer.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.detailedGeneratorData.length === 0) {
                    state.loadingGenDetails = true;
                }
                state.errorGenDetails = null;
            }).addCase(fetchBPLiveDataForCustomer.fulfilled, (state, action: PayloadAction<detailedGeneratorData[]>) => {
                state.loadingGenDetails = false; // Loading ends
                // Only generator data coming from excluded sources
                const excludedSources = ['bluepillar', 'BCH-Fuel'];

                /**
                 * ONLY bluepillar / BCH-Fuel data
                 */
                state.detailedGeneratorData = action.payload
                    .map((item) => ({
                        ...item,
                        equipment_data: item.equipment_data
                            ?.map((equipment) => ({
                                ...equipment,
                                data: equipment.data?.filter((d) =>
                                    excludedSources.includes(d.source_id)
                                )
                            }))
                            .filter((equipment) => equipment.data?.length)
                    }))
                    .filter((item) => item.equipment_data?.length);

                /**
                 * ONLY telemetry data (MQTT)
                 */
                state.detailedGeneratorDataForMQTT = action.payload
                    .map((item) => ({
                        ...item,
                        equipment_data: item.equipment_data
                            ?.map((equipment) => ({
                                ...equipment,
                                data: equipment.data?.filter((d) =>
                                    d.source_id.includes('telemetry')
                                )
                            }))
                            .filter((equipment) => equipment.data?.length)
                    }))
                    .filter((item) => item.equipment_data?.length);


            }).addCase(fetchBPLiveDataForCustomer.rejected, (state, action) => {
                state.loadingGenDetails = false;
                state.errorGenDetails = action.payload as string;
            }).addCase(fetchBPEqpCustkWh.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.kwhCustomerData.sites.length === 0) {
                    state.loadingKwhCustomerData = true;
                }
                state.errorKwhCustomerData = null;
            }).addCase(fetchBPEqpCustkWh.fulfilled, (state, action: PayloadAction<KwhData>) => {
                state.loadingKwhCustomerData = false; // Loading ends
                state.kwhCustomerData = action.payload; // Store session data
            }).addCase(fetchBPEqpCustkWh.rejected, (state, action) => {
                state.loadingKwhCustomerData = false;
                state.errorKwhCustomerData = action.payload as string;
            })
            // Factory data
            .addCase(fetchFactoryApiResponse.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                if (state.factoryApiResponse.length === 0) {
                    state.loadingFactoryApiResponse = true;
                }
            }).addCase(fetchFactoryApiResponse.fulfilled, (state, action: PayloadAction<any>) => {
                state.loadingFactoryApiResponse = false;
                state.factoryApiResponse = action.payload;
            }).addCase(fetchFactoryApiResponse.rejected, (state, action) => {
                state.loadingFactoryApiResponse = false;
                state.factoryApiResponseError = action.payload as string;
            })
    },
});

export const { updateCustomerResponse, clearPowerFuelData, clearDashboardData, clearGeneralData, setResponses, clearResponses, setLoading, setLoadingData } = dashboardSlice.actions;

export default dashboardSlice.reducer;
